import { useClient } from '@cortex/react-query';
import {
  describePausedSchedule,
  formatScheduleTagParam,
  managedScheduleBreakerCopy,
  managedWorkflowScheduleMinIntervalMs,
  parseScheduleTagParam,
  type IPaginated,
  type IScheduleTagPair,
  type Run,
  type WorkflowSchedule,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { readApiErrorBody, readManagedLockViolations } from './api-errors';
import { cronPresets } from './cron-presets';

/**
 * Query keys + shared mutations for workflow schedules (DEV-17470). Used by
 * the per-workflow Schedules panel and the org-wide Workflow schedules
 * table so pause / resume / run now behave identically on both surfaces.
 *
 * Every key starts with 'workflow-schedules' so one prefix invalidation
 * refreshes the org list and every per-workflow list at once.
 */
export const workflowSchedulesPrefix = ['workflow-schedules'] as const;
export const workflowSchedulesKey = (workflowId: string) =>
  ['workflow-schedules', workflowId] as const;
export const orgWorkflowSchedulesKey = ['workflow-schedules', 'org'] as const;
/** Last N runs a workflow's schedules fired (trigger SCHEDULED). */
export const scheduledRunsKey = (workflowId: string) => ['runs', 'scheduled', workflowId] as const;

/** How many scheduled runs the history list shows per workflow. */
export const scheduledRunHistoryLimit = 20;

export { describePausedSchedule };

/**
 * The status line under a schedule that is not firing, or null when there is
 * nothing to add: while it is enabled, and when the breaker's own sentence
 * (`managedScheduleBreakerCopy`, written to `lastError` on a Formant-managed
 * workflow) already says why. Both surfaces read the breaker's line once, as
 * the error, never as a status line above it as well.
 */
export function pausedScheduleLine(
  schedule: Pick<WorkflowSchedule, 'enabled' | 'pausedReason' | 'lastError'>,
): string | null {
  const copy = describePausedSchedule(schedule);
  if (!copy) return null;
  if (
    schedule.pausedReason === 'repeated-errors' &&
    schedule.lastError === managedScheduleBreakerCopy
  )
    return null;
  return copy;
}

/**
 * A schedule the workflow's own disable paused: nothing on the schedule can
 * bring it back, enabling the workflow does (DEV-17471).
 */
export function isPausedByWorkflow(
  schedule: Pick<WorkflowSchedule, 'enabled' | 'pausedReason'>,
): boolean {
  return !schedule.enabled && schedule.pausedReason === 'workflow-disabled';
}
export const pausedByWorkflowTitle = 'The workflow is disabled. Enable it to resume its updates.';

/** Title on every control a Formant-managed schedule keeps read-only. */
export const managedScheduleLockedTitle = 'Locked — managed by Formant';

/** The product floor for a Formant-managed schedule, in minutes: each fire is an org-wide gather. */
export const managedMinCadenceMinutes = managedWorkflowScheduleMinIntervalMs / 60_000;

/** Minutes between two fires of a preset, from its expression (every-N-minutes, every-N-hours, or a fixed time). */
function presetMinutes(expr: string): number {
  const every = /^\*\/(\d+) \* \* \* \*$/.exec(expr);
  if (every) return Number(every[1]);
  const hours = /^0 \*\/(\d+) \* \* \*$/.exec(expr);
  if (hours) return Number(hours[1]) * 60;
  return 60;
}

/** The cron presets a schedule may use: a managed one keeps to the product floor (a preset below it would only answer 400). */
export function cronPresetsFor(managed: boolean): { label: string; expr: string }[] {
  return managed
    ? cronPresets.filter((p) => presetMinutes(p.expr) >= managedMinCadenceMinutes)
    : cronPresets;
}

/** A skip ("skipped: …") is information, not a failure: it reads neutral. */
export function isSkipNote(lastError: string): boolean {
  return lastError.startsWith('skipped:');
}

/**
 * One line for a failed schedule action. A 409 `managed-workflow-locked`
 * names the locked part; a coded 409 (`fire-too-soon`, `schedule-disabled`)
 * carries its own sentence; anything else falls back to the error message.
 */
export async function describeScheduleActionError(err: unknown): Promise<string> {
  const locked = await readManagedLockViolations(err);
  if (locked.length > 0) return locked.join('; ');
  const body = await readApiErrorBody(err);
  if (body?.message) {
    return body.retryAfterSec
      ? `${body.message} (try again in ${Math.ceil(body.retryAfterSec / 60)} min)`
      : body.message;
  }
  return (err as Error).message;
}

/** The tag filter of the org-wide list (DEV-17502); see `scheduleListPath`. */
export interface IScheduleTagFilter {
  tags?: IScheduleTagPair[];
  /** false leaves untagged (org-wide) schedules out. */
  untagged?: boolean;
}

/**
 * `v1/schedules` with the repeatable `tag=key:value` param and `untagged=`.
 * The api keeps the schedules whose tags are a subset of the given pairs,
 * org-wide ones included unless `untagged` is false.
 */
export function scheduleListPath(filter: IScheduleTagFilter = {}): string {
  const qs = new URLSearchParams();
  for (const pair of filter.tags ?? []) qs.append('tag', formatScheduleTagParam(pair));
  if (filter.untagged !== undefined) qs.set('untagged', String(filter.untagged));
  const suffix = qs.toString();
  return suffix ? `v1/schedules?${suffix}` : 'v1/schedules';
}

/**
 * The filter box's text into pairs: comma-separated `key:value` entries.
 * Returns the pairs and the entries that are not `key:value`, so the page
 * can say which part it ignored.
 */
export function parseTagFilterText(text: string): {
  tags: IScheduleTagPair[];
  invalid: string[];
} {
  const tags: IScheduleTagPair[] = [];
  const invalid: string[] = [];
  for (const chunk of text.split(',')) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    const pair = parseScheduleTagParam(trimmed);
    if (pair) tags.push(pair);
    else invalid.push(trimmed);
  }
  return { tags, invalid };
}

export function useOrgWorkflowSchedules(filter: IScheduleTagFilter = {}) {
  const client = useClient();
  const path = scheduleListPath(filter);
  return useQuery({
    // The path carries the whole filter, so it is the key; the prefix still
    // matches `workflowSchedulesPrefix` for the shared invalidation.
    queryKey:
      path === 'v1/schedules' ? orgWorkflowSchedulesKey : [...orgWorkflowSchedulesKey, path],
    queryFn: () => client.http.get(path).json<WorkflowSchedule[]>(),
    refetchInterval: 30_000,
  });
}

/**
 * The tag keys the org already uses, for the dialog's key suggestions
 * (DEV-17502). Reads the org list only while `enabled`, so a closed dialog
 * costs nothing.
 */
export function useScheduleTagKeySuggestions(enabled: boolean): string[] {
  const client = useClient();
  const query = useQuery({
    queryKey: [...orgWorkflowSchedulesKey, 'tag-keys'],
    queryFn: () => client.http.get('v1/schedules').json<WorkflowSchedule[]>(),
    enabled,
    staleTime: 60_000,
  });
  return useMemo(() => {
    const keys = new Set<string>();
    for (const s of query.data ?? []) for (const key of Object.keys(s.tags ?? {})) keys.add(key);
    return [...keys].sort();
  }, [query.data]);
}

export function useScheduledRuns(workflowId: string) {
  const client = useClient();
  return useQuery({
    queryKey: scheduledRunsKey(workflowId),
    queryFn: () =>
      client.http
        .get(
          `v1/runs?workflowId=${encodeURIComponent(workflowId)}&trigger=SCHEDULED&limit=${scheduledRunHistoryLimit}`,
        )
        .json<IPaginated<Run>>(),
    refetchInterval: 30_000,
  });
}

export interface IScheduleRef {
  workflowId: string;
  id: string;
}

/**
 * Pause / resume, run now, delete. All three invalidate every schedule list;
 * run now also invalidates the runs lists so the Runs tab and the scheduled
 * run history pick the new run up.
 */
export function useWorkflowScheduleActions(
  opts: {
    onError?: (message: string) => void;
    onRunCreated?: (run: Run) => void;
  } = {},
) {
  const client = useClient();
  const qc = useQueryClient();

  const invalidateSchedules = async () => {
    try {
      await qc.invalidateQueries({ queryKey: workflowSchedulesPrefix });
    } catch (err) {
      console.error(err);
    }
  };
  const onError = async (err: unknown) => opts.onError?.(await describeScheduleActionError(err));

  const setEnabled = useMutation({
    mutationFn: (vars: IScheduleRef & { enabled: boolean }) =>
      client.http
        .patch(`v1/workflows/${vars.workflowId}/schedules/${vars.id}`, {
          json: { enabled: vars.enabled },
        })
        .json<WorkflowSchedule>(),
    onSuccess: invalidateSchedules,
    onError,
  });

  const runNow = useMutation({
    mutationFn: (vars: IScheduleRef) =>
      client.http.post(`v1/workflows/${vars.workflowId}/schedules/${vars.id}/trigger`).json<Run>(),
    onSuccess: async (run) => {
      await invalidateSchedules();
      try {
        await qc.invalidateQueries({ queryKey: ['runs'] });
      } catch (err) {
        console.error(err);
      }
      opts.onRunCreated?.(run);
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (vars: IScheduleRef) =>
      client.http.delete(`v1/workflows/${vars.workflowId}/schedules/${vars.id}`),
    onSuccess: invalidateSchedules,
    onError,
  });

  return {
    setEnabled,
    runNow,
    remove,
    busy: setEnabled.isPending || runNow.isPending || remove.isPending,
  };
}

/**
 * Copy shown wherever schedules are listed. Schedules hang off a workflow,
 * never off a device: which robot a scheduled run touches is the workflow's
 * decision at run time (its input, its tools, its graph).
 */
export const scheduleOwnershipNote =
  'A schedule belongs to the workflow. When it fires, the workflow decides which robot or device the run works on; schedules are not set per device.';

/** Copy on a Formant-managed workflow's Schedules tab in place of the create button. */
export const managedScheduleNote =
  'Formant provisions this workflow’s schedule. Its cadence and pause are yours; its input and name are the template’s.';
